import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Lock, LogIn, User } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

export const LoginPage: React.FC = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as { from?: { pathname?: string; search?: string } } | null;
  const redirectTo = `${state?.from?.pathname || '/'}${state?.from?.search || ''}`;

  const [loginValue, setLoginValue] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(loginValue, password);
      navigate(redirectTo, { replace: true });
    } catch {
      setError('Unable to sign in with those credentials.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-3 py-6 sm:px-6">
      <Card className="w-full max-w-md">
        <CardContent className="space-y-6 p-4 sm:p-6">
          <div className="space-y-1">
            <div className="inline-flex items-center gap-2 border-2 border-foreground bg-primary px-3 py-1 font-black">
              <LogIn className="w-4 h-4" />
              Sign in
            </div>
            <h1 className="text-2xl font-black text-foreground">ABR streaming</h1>
          </div>

          <form onSubmit={(event) => void handleSubmit(event)} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="login-name">Login</Label>
              <div className="relative">
                <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/50" />
                <Input
                  id="login-name"
                  type="text"
                  autoComplete="username"
                  value={loginValue}
                  onChange={(event) => setLoginValue(event.target.value)}
                  className="pl-9"
                  required
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="login-password">Password</Label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/50" />
                <Input
                  id="login-password"
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="pl-9"
                  required
                />
              </div>
            </div>

            {error && (
              <p className="border-2 border-destructive bg-destructive text-destructive-foreground px-3 py-2 text-sm font-bold">
                {error}
              </p>
            )}

            <Button type="submit" className="w-full" disabled={submitting}>
              <LogIn className="w-4 h-4" />
              {submitting ? 'Signing in...' : 'Sign in'}
            </Button>
          </form>

          <div className="flex flex-wrap items-center justify-between gap-3 text-sm font-bold">
            <Link to="/" className="underline">Browse videos</Link>
            <Link to="/register" state={location.state} className="underline">Create account</Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
